import { z } from "zod";

import { LeadRelationshipResponse, leadRelationshipResponseSchema } from "@signalist/shared-types";

import { api } from "@/redux/api";

/**
 * The lead-relationship payload behind a list person: their active campaign, past outreach,
 * connections and the other lists they sit on (PREC-3547) — plus the pause that acts on it
 * (PREC-3906).
 *
 * Injected from its own module rather than from `list.api.ts`, which is far past the
 * 500-line ceiling and may only shrink. The endpoint is self-contained — one query, one
 * shared response schema — so it extracts cleanly, and both consumers (the lead-card hover
 * and the "Add to CRM" list picker) import their hook from here.
 */
export const pauseOutreachResponseSchema = z.object({
	listPersonId: z.number(),
	stopCampaignAt: z.string().nullable(),
	movedSchedules: z.number(),
});

export type PauseOutreachResponse = z.infer<typeof pauseOutreachResponseSchema>;

interface PauseLeadOutreachArgs {
	/** The row that carries the running campaign — not necessarily the one on screen. */
	listPersonId: number;
	/**
	 * The row the panel is showing, when it differs. Its relationship entry is keyed by its own id,
	 * so without invalidating it too the panel would keep rendering the campaign it just stopped.
	 */
	panelListPersonId?: number;
}

/**
 * Both directions can act on a row other than the one the panel is showing, and the relationship
 * cache is keyed by the *queried* row — so both entries have to go, or the panel keeps rendering
 * the campaign state it just changed.
 */
const invalidateBothRows = (
	result: unknown,
	error: unknown,
	arg: PauseLeadOutreachArgs
): { type: "ListPerson"; id: number }[] =>
	[arg.listPersonId, arg.panelListPersonId]
		.filter((id): id is number => typeof id === "number")
		.map((id) => ({ type: "ListPerson" as const, id }));

export const listRelationshipApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getLeadRelationship: builder.query<LeadRelationshipResponse, { listPersonId: number }>({
			query: ({ listPersonId }) => ({
				url: `list_person/${listPersonId}/relationships`,
				method: "GET",
				responseSchema: leadRelationshipResponseSchema,
			}),
			providesTags: (result, error, arg) => [{ type: "ListPerson", id: arg.listPersonId }],
		}),
		/**
		 * Stop one lead's campaign: writes the per-person cut-off and holds the steps still queued for
		 * it, so the lead stops being "in an active campaign" instead of merely being barred from
		 * scheduling the *next* step. Reversible via {@link resumeLeadOutreach}.
		 */
		pauseLeadOutreach: builder.mutation<PauseOutreachResponse, PauseLeadOutreachArgs>({
			query: ({ listPersonId }) => ({
				url: `list_person/${listPersonId}/pause-outreach`,
				method: "POST",
				responseSchema: pauseOutreachResponseSchema,
			}),
			invalidatesTags: invalidateBothRows,
		}),
		/** Lift the cut-off and release the held steps. */
		resumeLeadOutreach: builder.mutation<PauseOutreachResponse, PauseLeadOutreachArgs>({
			query: ({ listPersonId }) => ({
				url: `list_person/${listPersonId}/resume-outreach`,
				method: "POST",
				responseSchema: pauseOutreachResponseSchema,
			}),
			invalidatesTags: invalidateBothRows,
		}),
	}),
});

export const {
	useGetLeadRelationshipQuery,
	useLazyGetLeadRelationshipQuery,
	usePauseLeadOutreachMutation,
	useResumeLeadOutreachMutation,
} = listRelationshipApi;
